import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { BalanceScaleIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { NotFound } from '@app/NotFound/NotFound';
import { useUserContext } from '../../../contexts/user/UserContext';
import type { SalesInvoiceBasis } from '../../../types/models/Reconciliation';
import { defaultMonth, isSelectableMonth, isSelectableYear } from './reconciliationPeriod';
import ReconciliationOverviewTab from './ReconciliationOverviewTab';
import MollieJournalTab from './MollieJournalTab';
import SettlementsTab from './SettlementsTab';
import MollieInvoicesTab from './MollieInvoicesTab';
import SalesJournalTab from './SalesJournalTab';
import AccountingEntriesTab from './AccountingEntriesTab';

// One key per tab, in display order; the default (`overview`) carries no `?tab=`.
const TAB_KEYS = ['overview', 'journal', 'settlements', 'mollie-invoices', 'sales', 'accounting'] as const;
type TabKey = (typeof TAB_KEYS)[number];
const isTabKey = (value: unknown): value is TabKey =>
  typeof value === 'string' && (TAB_KEYS as readonly string[]).includes(value);

/**
 * Admin › Comptabilité — the accountant's month views tying the sales invoices (Facturier) to what Mollie
 * does with the money (docs/api-types/admin-reconciliation.md). The only French admin screens. Six tabs:
 * Vue du mois · Journal Mollie · Versements (drill-down to /admin/reconciliation/settlements/:id) ·
 * Factures Mollie · Journal des ventes · Comptabilité (the entries to encode or check in WinAuditor, with
 * the « Plan comptable » settings modal).
 *
 * State lives in the URL so a view can be linked and the payout breadcrumb can come back to it:
 * `?tab=` (none for the overview), `month=` (the four month tabs share it; default: the previous Brussels
 * month), `year=` (the two year tabs share it; default: the selected month's year) and `basis=paid` (the
 * sales journal; default `issued`). Every read is live and expensive (Mollie + Facturier, 25 s budget):
 * tabs mount lazily (`mountOnEnter`) and a tab only reads while it is the active one, when its month / year
 * / basis differs from its last read — so changing the month on one tab never fires the others.
 *
 * Gating is enforced twice (defense in depth): AppLayout hides the Admin nav group unless
 * `user.is_ancalagon_staff`, and this page renders <NotFound /> for anyone else. The backend /admin routes
 * enforce it authoritatively.
 */
export function Reconciliation() {
  const auth = useAuth();
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;
  const [searchParams, setSearchParams] = useSearchParams();
  // The overview's « Voir dans le journal » opens the journal on its flagged rows.
  const [journalFlaggedOnly, setJournalFlaggedOnly] = useState(false);

  const requestedTab = searchParams.get('tab');
  const activeTab: TabKey = isTabKey(requestedTab) ? requestedTab : 'overview';
  const requestedMonth = searchParams.get('month');
  const month = isSelectableMonth(requestedMonth) ? requestedMonth : defaultMonth();
  const requestedYear = searchParams.get('year');
  const year = isSelectableYear(requestedYear) ? Number(requestedYear) : Number(month.slice(0, 4));
  const basis: SalesInvoiceBasis = searchParams.get('basis') === 'paid' ? 'paid' : 'issued';

  // `replace` so tab and period switches don't pile up in the history.
  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    setSearchParams(next, { replace: true });
  };
  const selectTab = (key: TabKey) => update({ tab: key === 'overview' ? null : key });
  const setMonth = (m: string) => update({ month: m });
  const setYear = (y: number) => update({ year: String(y) });
  const setBasis = (b: SalesInvoiceBasis) => update({ basis: b === 'paid' ? 'paid' : null });

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }
  if (!isStaff) {
    return <NotFound />;
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb>
          <BreadcrumbItem>Admin</BreadcrumbItem>
          <BreadcrumbItem isActive>Comptabilité</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <BalanceScaleIcon />
          </Icon>
          Comptabilité
        </Content>
        <Content component="p">
          L&apos;argent encaissé via Mollie face aux factures de vente, mois par mois : versements, frais, factures
          Mollie et règlement de chaque facture.
        </Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Tabs
          activeKey={activeTab}
          onSelect={(_event, tabKey) => {
            if (isTabKey(tabKey)) {
              selectTab(tabKey);
            }
          }}
          aria-label="Sections de la comptabilité"
          mountOnEnter
        >
          <Tab eventKey="overview" title={<TabTitleText>Vue du mois</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <ReconciliationOverviewTab
                month={month}
                onMonthChange={setMonth}
                isActive={activeTab === 'overview'}
                onOpenFlaggedJournal={() => {
                  setJournalFlaggedOnly(true);
                  selectTab('journal');
                }}
              />
            </div>
          </Tab>
          <Tab eventKey="journal" title={<TabTitleText>Journal Mollie</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <MollieJournalTab
                month={month}
                onMonthChange={setMonth}
                isActive={activeTab === 'journal'}
                flaggedOnly={journalFlaggedOnly}
                onFlaggedOnlyChange={setJournalFlaggedOnly}
              />
            </div>
          </Tab>
          <Tab eventKey="settlements" title={<TabTitleText>Versements</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <SettlementsTab year={year} onYearChange={setYear} isActive={activeTab === 'settlements'} />
            </div>
          </Tab>
          <Tab eventKey="mollie-invoices" title={<TabTitleText>Factures Mollie</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <MollieInvoicesTab year={year} onYearChange={setYear} isActive={activeTab === 'mollie-invoices'} />
            </div>
          </Tab>
          <Tab eventKey="sales" title={<TabTitleText>Journal des ventes</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <SalesJournalTab
                month={month}
                onMonthChange={setMonth}
                basis={basis}
                onBasisChange={setBasis}
                isActive={activeTab === 'sales'}
              />
            </div>
          </Tab>
          <Tab eventKey="accounting" title={<TabTitleText>Comptabilité</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <AccountingEntriesTab month={month} onMonthChange={setMonth} isActive={activeTab === 'accounting'} />
            </div>
          </Tab>
        </Tabs>
      </PageSection>
    </>
  );
}

export default Reconciliation;
